// WhatsApp lead distribution — "Shuffle Deck" round-robin.
//
// Each VSL page (/vsl and /vsl-2) keeps its OWN shuffled deck of sales numbers
// in localStorage. A visitor is dealt ONE number for the whole visit (cached in
// sessionStorage), so every WhatsApp button on that page reaches the same rep.
// When a deck is exhausted it reshuffles, keeping distribution even across
// repeat draws in the same browser.

// Sales numbers in WhatsApp format (digits only — no +, spaces or dashes).
// Every VSL route currently routes to a single number: +1 (786) 935-2183.
const SALES_NUMBERS: string[] = [
  '17869352183', // 🇺🇸 +1 (786) 935-2183
]

type PageKey = string

// Per-page number overrides. A route listed here always uses its dedicated
// number instead of the shuffled deck. Empty for now — all routes share the
// single SALES_NUMBERS entry above.
const PAGE_NUMBER_OVERRIDES: Record<string, string> = {}

// Storage version — bump when the SALES_NUMBERS change so any deck/assignment
// cached under the old numbers is discarded and visitors pick up the new ones.
const STORE_VERSION = 'v3'

const deckKey = (page: PageKey) => `vsl_wa_deck_${STORE_VERSION}_${page}`
const ptrKey = (page: PageKey) => `vsl_wa_deck_ptr_${STORE_VERSION}_${page}`
const assignedKey = (page: PageKey) =>
  `vsl_wa_assigned_${STORE_VERSION}_${page}`

// Fisher–Yates shuffle over the index list.
function shuffledIndices(): number[] {
  const idx = SALES_NUMBERS.map((_, i) => i)
  for (let i = idx.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[idx[i], idx[j]] = [idx[j], idx[i]]
  }
  return idx
}

// Draw the next number off the page's deck, reshuffling when exhausted.
function drawNextNumber(page: PageKey): string {
  // Single number → no deck logic needed.
  if (SALES_NUMBERS.length <= 1) return SALES_NUMBERS[0]
  let deck: number[]
  try {
    deck = JSON.parse(localStorage.getItem(deckKey(page)) || 'null')
    if (!Array.isArray(deck) || deck.length !== SALES_NUMBERS.length) {
      deck = shuffledIndices()
      localStorage.setItem(deckKey(page), JSON.stringify(deck))
      localStorage.setItem(ptrKey(page), '0')
    }
  } catch {
    deck = shuffledIndices()
  }
  let ptr = 0
  try {
    ptr = parseInt(localStorage.getItem(ptrKey(page)) || '0', 10) || 0
  } catch {}
  if (ptr >= deck.length) {
    // Deck exhausted → reshuffle and start over.
    deck = shuffledIndices()
    ptr = 0
    try {
      localStorage.setItem(deckKey(page), JSON.stringify(deck))
    } catch {}
  }
  const numberIndex = deck[ptr] ?? 0
  try {
    localStorage.setItem(ptrKey(page), String(ptr + 1))
  } catch {}
  return SALES_NUMBERS[numberIndex] ?? SALES_NUMBERS[0]
}

/**
 * Returns the number assigned to this visitor for the given page. The same
 * number is reused for the whole visit (one rep per contact, per page).
 */
export function getAssignedNumber(page: PageKey): string {
  // Pinned routes bypass the shuffle deck entirely.
  const override = PAGE_NUMBER_OVERRIDES[page]
  if (override) return override
  try {
    const cached = sessionStorage.getItem(assignedKey(page))
    if (cached) return cached
  } catch {}
  const drawn = drawNextNumber(page)
  try {
    sessionStorage.setItem(assignedKey(page), drawn)
  } catch {}
  return drawn
}

/**
 * Builds a WhatsApp deep link for the visitor's assigned rep on the given page.
 * `encodedText` must already be URL-encoded (the page passes the exact prefilled
 * message string used today).
 */
export function buildWhatsAppLink(page: PageKey, encodedText: string): string {
  const phone = getAssignedNumber(page)
  return `https://api.whatsapp.com/send?phone=${phone}&text=${encodedText}`
}
